import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { ApplicantPipeline } from "../components/ApplicantPipeline";
import { RoleBrief } from "../components/RoleBrief";
import { toast } from "sonner";
import { Briefcase, MapPin, ArrowLeft, Send } from "lucide-react";

export default function RoleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [applying, setApplying] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/roles/${id}`);
        setR(data);
      } catch { toast.error("Not found"); }
    })();
  }, [id]);

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/roles/${id}/apply`, { note: note || null });
      if (data.status === "already_applied") toast.info("You've already applied");
      else toast.success("Application submitted");
      setR((x) => ({ ...x, my_application: { status: "applied" } }));
    } catch { toast.error("Failed to apply"); }
    finally { setApplying(false); }
  };

  if (!r) return <Skeleton className="h-64" />;
  const applied = !!r.my_application;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="role-detail">
      <Button variant="ghost" onClick={() => nav(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>

      <Card className="p-8">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div>
            <div className="label-caps">{r.role_family}</div>
            <h1 className="text-2xl md:text-3xl tracking-tighter font-semibold mt-2" data-testid="role-title-heading">{r.title}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{r.org_name}</span>
              {r.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.location}</span>}
              {r.remote_policy && <span>{r.remote_policy}</span>}
            </div>
          </div>
          {r.is_member ? (
            <Badge variant="outline" className="h-fit font-mono" data-testid="role-applicant-count">{r.applicant_count} applicant{r.applicant_count === 1 ? "" : "s"}</Badge>
          ) : applied ? (
            <Badge className="h-fit capitalize" data-testid="my-application-status">Application · {r.my_application.status}</Badge>
          ) : null}
        </div>
        {!r.is_member && !applied && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="apply-box">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A short note to the hiring team (optional)" data-testid="apply-note" />
            <Button onClick={apply} disabled={applying} className="rounded-full sm:self-end" data-testid="apply-btn">
              <Send className="h-4 w-4 mr-1" />{applying ? "Applying…" : "Apply"}
            </Button>
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          {(r.salary_min || r.salary_max) && (
            <div>
              <div className="label-caps mb-1">Compensation</div>
              <div className="font-mono">${(r.salary_min || 0).toLocaleString()} – ${(r.salary_max || 0).toLocaleString()}</div>
            </div>
          )}
          {(r.equity_min || r.equity_max) && (
            <div>
              <div className="label-caps mb-1">Equity</div>
              <div className="font-mono">{r.equity_min || 0}% – {r.equity_max || 0}%</div>
            </div>
          )}
          {r.experience_years && (
            <div>
              <div className="label-caps mb-1">Experience</div>
              <div className="font-mono">{r.experience_years}+ years</div>
            </div>
          )}
        </div>

        <div className="mt-8">
          <div className="label-caps mb-2">Description</div>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.description}</p>
        </div>

        {r.required_skills?.length > 0 && (
          <div className="mt-6">
            <div className="label-caps mb-2">Required skills</div>
            <div className="flex flex-wrap gap-1.5">
              {r.required_skills.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
            </div>
          </div>
        )}
        {r.industries?.length > 0 && (
          <div className="mt-4">
            <div className="label-caps mb-2">Industries</div>
            <div className="flex flex-wrap gap-1.5">
              {r.industries.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
            </div>
          </div>
        )}
      </Card>
      {r.is_member && <ApplicantPipeline roleId={id} />}
      <RoleBrief roleId={id} />
    </div>
  );
}
